import { useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { useLocalStorage } from "@nens/react/hooks";

import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { usePublicationCommentListQuery } from "@/queries/query-publication-comment";

import { PublicatonMapId } from "../../../types/publication";
import { useAddComment, useCreateCommentThread } from "../api/add";
import { useEditCommentMessage } from "../api/edit-message";
import { useToggleCommentReaction } from "../api/reaction";
import { useRemoveCommentThread } from "../api/remove";
import { useRemoveCommentMessage } from "../api/remove-message";
import { useResolveComment } from "../api/resolve";

export const PUBLICATION_SELECTED_COMMENT_KEY = "selectedComment";

export function usePublicationComment(publicationId: string, mapId: PublicatonMapId) {
  const toast = useToast();
  const intl = useIntl();
  const [showResolved, setShowResolved] = useLocalStorage("rana.show_resolved", false);

  const { [publicationId]: map } = useMap();
  const commentThreadsQuery = usePublicationCommentListQuery(publicationId, mapId);
  const commentThreads = commentThreadsQuery.data?.items ?? null;

  const [selectedCommentThreadId, setSelectedCommentThreadId] = useRanaSearchState<string | null>(
    PUBLICATION_SELECTED_COMMENT_KEY,
    null,
    ["string", "null"],
    { dontEverSaveInHistory: true }
  );
  const [creatingCommentAt, setCreatingCommentAt] = useState<[number, number] | null>(null);
  const [isAddingComment, setIsAddingComment] = useState(false);

  useEffect(() => {
    if (creatingCommentAt && (!isAddingComment || selectedCommentThreadId)) {
      setCreatingCommentAt(null);
    }
  }, [creatingCommentAt, isAddingComment, selectedCommentThreadId]);

  const selectedCommentThread = commentThreads?.find(({ id }) => id === selectedCommentThreadId);

  // Move to selected comment.
  useEffect(() => {
    if (selectedCommentThread && map)
      map.flyTo({
        center: selectedCommentThread.location.coordinates as [number, number],
        speed: 0.2,
      });
  }, [selectedCommentThread, map]);

  const addCommentToMapViaCoords = (lng: number, lat: number) => {
    setIsAddingComment(true);
    setCreatingCommentAt([lng, lat]);
    setSelectedCommentThreadId(null);
  };

  const addCommentToMap = (e: maplibregl.MapLayerMouseEvent) => {
    if (!isAddingComment) return null;

    // If already creating comment or a thread is selected this function will not trigger. That is handled in `RanaMapSelf` -> `onClick`
    // It is handled there because otherwise the maps own onClick would still trigger
    setCreatingCommentAt([e.lngLat.lng, e.lngLat.lat]);
    setSelectedCommentThreadId(null);
  };

  const { mutate: createCommentThread } = useCreateCommentThread(publicationId, mapId, ({ id }) => {
    setCreatingCommentAt(null);
    setSelectedCommentThreadId(id);
  });
  const { mutate: addCommentToThread } = useAddComment(publicationId, mapId);

  const { mutate: removeCommentThread } = useRemoveCommentThread(publicationId, () => {
    setSelectedCommentThreadId(null);
  });

  const { mutate: removeCommentMessage } = useRemoveCommentMessage(publicationId);
  const { mutate: toggleCommentReaction } = useToggleCommentReaction(publicationId, mapId);

  const { mutate: editCommentMessage } = useEditCommentMessage(publicationId, () =>
    toast({
      title: intl.formatMessage({
        id: "success",
        defaultMessage: "Success!",
      }),
      description: intl.formatMessage({
        id: "success.edited_comment",
        defaultMessage: "Successfully edited comment.",
      }),
    })
  );

  const { mutate: resolveComment } = useResolveComment(publicationId);

  return {
    publicationId,
    commentThreads,
    selectedCommentThread,
    creatingCommentAt,
    setCreatingCommentAt,
    setSelectedCommentThreadId,
    addCommentToMap,
    addCommentToMapViaCoords,
    createCommentThread,
    addCommentToThread,
    isAddingComment,
    setIsAddingComment,
    removeCommentThread,
    removeCommentMessage,
    toggleCommentReaction,
    editCommentMessage,
    resolveComment,
    showResolved,
    setShowResolved,
  };
}

export type UsePublicationCommentReturn = ReturnType<typeof usePublicationComment>;
